import { TNpsQuestion, TNpsQuestionValue } from '@cannect/services/resources/nps'
import * as Style from './styles'

interface QuestionFieldProps {
  question: TNpsQuestion
  value: TNpsQuestionValue
  onChange: (value: TNpsQuestionValue) => void
  extraText?: string
  onExtraTextChange?: (text: string) => void
}

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinejoin="round"
      strokeLinecap="round"
      d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.32.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.32-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
    />
  </svg>
)

function StarRating({ question, value, onChange }: QuestionFieldProps) {
  const min = question.min_value ?? 1
  const max = question.max_value ?? 5
  const current = typeof value === 'number' ? value : null
  const stars = Array.from({ length: max - min + 1 }, (_, i) => min + i)

  return (
    <Style.StarsRow role="radiogroup" aria-label={question.label}>
      {stars.map((star) => {
        const active = current !== null && star <= current
        return (
          <Style.StarButton
            key={star}
            type="button"
            active={active}
            aria-label={`${star} estrelas`}
            role="radio"
            aria-checked={current === star}
            onClick={() => onChange(star)}>
            <StarIcon filled={active} />
          </Style.StarButton>
        )
      })}
    </Style.StarsRow>
  )
}

function NpsScale({ question, value, onChange }: QuestionFieldProps) {
  const min = question.min_value ?? 0
  const max = question.max_value ?? 10
  const current = typeof value === 'number' ? value : null
  const numbers = Array.from({ length: max - min + 1 }, (_, i) => min + i)

  const toneFor = (n: number): 'red' | 'yellow' | 'green' => {
    if (n <= 6) return 'red'
    if (n <= 8) return 'yellow'
    return 'green'
  }

  return (
    <Style.NpsScaleRow role="radiogroup" aria-label={question.label}>
      {numbers.map((n) => (
        <Style.NpsScaleButton
          key={n}
          type="button"
          tone={toneFor(n)}
          active={current === n}
          role="radio"
          aria-checked={current === n}
          onClick={() => onChange(n)}>
          {n}
        </Style.NpsScaleButton>
      ))}
    </Style.NpsScaleRow>
  )
}

function SingleChoice({ question, value, onChange, extraText, onExtraTextChange }: QuestionFieldProps) {
  const options = question.options ?? []
  const current = typeof value === 'string' ? value : null
  const selectedOption = options.find((o) => o.value === current)
  const showExtraText = selectedOption?.allow_text === true

  const handleSelect = (optValue: string) => {
    const opt = options.find((o) => o.value === optValue)
    if (!opt?.allow_text) onExtraTextChange?.('')
    onChange(optValue)
  }

  return (
    <Style.ChoiceList>
      {options.map((opt) => (
        <Style.ChoiceItem key={opt.value} selected={current === opt.value}>
          <input
            type="radio"
            name={question.uuid}
            value={opt.value}
            checked={current === opt.value}
            onChange={() => handleSelect(opt.value)}
          />
          <span>{opt.label}</span>
        </Style.ChoiceItem>
      ))}
      {showExtraText && (
        <Style.OptionExtraTextInput
          type="text"
          placeholder="Especifique..."
          value={extraText ?? ''}
          onChange={(e) => onExtraTextChange?.(e.target.value)}
          autoFocus
        />
      )}
    </Style.ChoiceList>
  )
}

function MultiChoice({ question, value, onChange, extraText, onExtraTextChange }: QuestionFieldProps) {
  const options = question.options ?? []
  const current = Array.isArray(value) ? value : []
  const showExtraText = options.some((o) => o.allow_text && current.includes(o.value))

  const toggle = (optValue: string) => {
    const opt = options.find((o) => o.value === optValue)
    const willRemove = current.includes(optValue)
    if (willRemove && opt?.allow_text) onExtraTextChange?.('')
    onChange(willRemove ? current.filter((v) => v !== optValue) : [...current, optValue])
  }

  return (
    <Style.ChoiceList>
      {options.map((opt) => {
        const selected = current.includes(opt.value)
        return (
          <Style.ChoiceItem key={opt.value} selected={selected}>
            <input type="checkbox" value={opt.value} checked={selected} onChange={() => toggle(opt.value)} />
            <span>{opt.label}</span>
          </Style.ChoiceItem>
        )
      })}
      {showExtraText && (
        <Style.OptionExtraTextInput
          type="text"
          placeholder="Especifique..."
          value={extraText ?? ''}
          onChange={(e) => onExtraTextChange?.(e.target.value)}
          autoFocus
        />
      )}
    </Style.ChoiceList>
  )
}

function ShortText({ question, value, onChange }: QuestionFieldProps) {
  return (
    <Style.TextInput
      type="text"
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      aria-required={question.required}
      placeholder=""
    />
  )
}

function LongText({ question, value, onChange }: QuestionFieldProps) {
  return (
    <Style.TextArea
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      aria-required={question.required}
    />
  )
}

export default function QuestionField(props: QuestionFieldProps) {
  const { question } = props

  return (
    <Style.QuestionWrapper>
      <Style.QuestionLabel htmlFor={question.uuid}>
        {question.label}
        {question.required && <span className="required">*</span>}
      </Style.QuestionLabel>
      {question.help_text && <Style.HelpText>{question.help_text}</Style.HelpText>}

      {question.type === 'star_rating' && <StarRating {...props} />}
      {question.type === 'nps_scale' && <NpsScale {...props} />}
      {question.type === 'single_choice' && <SingleChoice {...props} />}
      {question.type === 'multi_choice' && <MultiChoice {...props} />}
      {question.type === 'short_text' && <ShortText {...props} />}
      {question.type === 'long_text' && <LongText {...props} />}
    </Style.QuestionWrapper>
  )
}

